import { themeV2 as theme } from '@features/executive-report/domain/theme-v2';
import { formatWithSign } from '@features/executive-report/utils/format-with-sign';
import { slideMasters } from '@features/executive-report/utils/slide-masters';
import {
  ShapeProps,
  Slide,
  TextProps,
  TextPropsTextConfig,
  ZERO_WIDTH_SPACE,
} from '@features/powerpoint';
import { formatters, ValueInput } from '@utils/formattersV2';
import { cardBackground } from './components/card-background';
import { arrowFromValue } from './components/arrow-from-value';

const columnHeaders = ['Objective', 'Key Results', 'Key Drivers'] as const;

const COLUMN_X_OFFSET = 4.22;

const buildColumnHeader = (text: string, index: number): TextProps => {
  return {
    text: text.toLocaleUpperCase(),
    options: {
      fill: { color: theme.colors.commandBlue, type: 'solid' },
      fontSize: 14,
      align: 'center',
      valign: 'middle',
      bold: true,
      color: theme.colors.white,
      h: 0.5,
      w: 4,
      x: 0.45 + index * COLUMN_X_OFFSET,
      y: 1.56,
    },
  };
};

const objectiveListText = (text: string): TextPropsTextConfig[] => [
  {
    text: ZERO_WIDTH_SPACE,
    options: {
      bullet: { indent: 20 },
      color: theme.colors.inmarketGreen,
      bold: true,
      fontSize: 28,
      paraSpaceBefore: 2,
    },
  },
  {
    text,
  },
];

type SlideProps = {
  navbarText: TextProps;
  incrementalLift: ValueInput;
  incrementalSales: ValueInput;
  iroas: ValueInput;
  keyDrivers: { shapes: ShapeProps[]; texts: TextProps[] };
  objectives: string[];
};

export const salesCampaignOverviewSlide2 = (props: SlideProps): Slide => {
  const keyResultsArrow = arrowFromValue(props.incrementalLift ?? 0, {
    h: 0.5,
    w: 0,
    x: 7.1,
    y: 3.03,
  });

  return {
    slideMaster: slideMasters.light,
    color: theme.colors.metricBlue,
    shapes: [
      // column triangles
      ...columnHeaders.map(
        (_, index): ShapeProps => ({
          shapeName: 'triangle',
          options: {
            fill: { color: theme.colors.commandBlue, type: 'solid' },
            h: 0.23,
            w: 0.41,
            x: 2.34 + index * COLUMN_X_OFFSET,
            y: 2.05,
            rotate: 180,
          },
        })
      ),
      // key results card
      cardBackground({
        h: 4.59,
        w: 4,
        x: 4.67,
        y: 2.45,
      }),
      // key results incremental lift arrow
      ...(keyResultsArrow ? [keyResultsArrow] : []),
      // total incremental sales box
      {
        shapeName: 'roundRect',
        options: {
          line: { color: theme.colors.inmarketGreen, width: 1 },
          h: 1.53,
          w: 3.67,
          x: 4.82,
          y: 3.88,
        },
      },
      // key drivers shapes
      ...props.keyDrivers.shapes,
    ],
    texts: [
      props.navbarText,
      {
        text: 'Campaign Overview',
        options: {
          fontSize: 36,
          valign: 'middle',
          bold: true,
          h: 0.75,
          w: 12.67,
          x: 0.33,
          y: 0.59,
        },
      },
      ...columnHeaders.map((text, index) => buildColumnHeader(text, index)),
      // objective bullets
      {
        text: props.objectives.flatMap(objectiveListText),
        options: {
          fontSize: 14,
          valign: 'top',
          h: 3.39,
          w: 3.75,
          x: 0.45,
          y: 2.77,
        },
      },
      // key results
      {
        text: [
          {
            text: 'TOTAL ',
          },
          {
            text: 'INCREMENTAL LIFT %',
            options: {
              bold: true,
            },
          },
        ],
        options: {
          fontSize: 14,
          valign: 'middle',
          h: 0.34,
          w: 3.12,
          x: 4.95,
          y: 2.68,
        },
      },
      {
        text: formatWithSign(props.incrementalLift, formatters.lift),
        options: {
          fontSize: 36,
          valign: 'middle',
          bold: true,
          h: 0.71,
          w: 2.03,
          x: 4.95,
          y: 2.95,
        },
      },
      {
        text: [
          {
            text: 'TOTAL ',
          },
          {
            text: 'INCREMENTAL SALES',
            options: {
              bold: true,
            },
          },
        ],
        options: {
          fontSize: 14,
          valign: 'middle',
          h: 0.34,
          w: 3.11,
          x: 4.95,
          y: 4.09,
        },
      },
      {
        text: formatters.currency(props.incrementalSales),
        options: {
          fontSize: 36,
          valign: 'middle',
          color: theme.colors.inmarketGreen,
          bold: true,
          h: 0.84,
          w: 3.19,
          x: 4.95,
          y: 4.4,
        },
      },
      {
        text: [
          {
            text: 'IROAS',
            options: {
              bold: true,
              breakLine: true,
              fontSize: 14,
            },
          },
          {
            text: 'INCREMENTAL RETURN ON AD SPEND',
            options: {
              fontSize: 11,
            },
          },
        ],
        options: {
          valign: 'middle',
          h: 0.52,
          w: 3.28,
          x: 4.96,
          y: 5.73,
        },
      },
      {
        text: formatters.currency(props.iroas, { full: true }),
        options: {
          fontSize: 36,
          valign: 'middle',
          h: 0.71,
          w: 3.19,
          x: 4.95,
          y: 6.15,
        },
      },
      // key drivers
      {
        text: [
          {
            text: 'More is ',
          },
          {
            text: 'more.',
            options: {
              bold: true,
              color: theme.colors.inmarketGreen,
            },
          },
        ],
        options: {
          align: 'center',
          valign: 'middle',
          fontSize: 32,
          h: 0.77,
          w: 4,
          x: 8.88,
          y: 2.31,
        },
      },
      ...props.keyDrivers.texts,
    ],
  };
};
